import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Lock, ChartLineUp, Users, Eye, EyeSlash } from "@phosphor-icons/react";

const Auth = ({ login }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get("reset_token");
  // modes: login | register | forgot | reset
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [formData, setFormData] = useState({ email: "", password: "", name: "", role: "customer", new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (resetToken) {
      axios.get(`${API}/auth/verify-reset-token?token=${resetToken}`).then(r => {
        if (r.data?.valid) setTokenEmail(r.data.email || "");
        else {
          toast.error(r.data?.reason === "expired" ? "Reset link expired. Please request a new one." : "Invalid reset link.");
          setMode("forgot");
        }
      }).catch(() => setMode("forgot"));
    }
  }, [resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const response = await axios.post(`${API}/auth/login`, { email: formData.email, password: formData.password });
        login(response.data);
        toast.success("Login successful!");
        navigate("/dashboard");
      } else if (mode === "register") {
        await axios.post(`${API}/auth/register`, formData);
        toast.success("Registration successful! Please login.");
        setMode("login");
      } else if (mode === "forgot") {
        const r = await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
        toast.success(r.data?.message || "Check your email for reset instructions.");
        setMode("login");
      } else if (mode === "reset") {
        if (formData.new_password !== formData.confirm_password) { toast.error("Passwords don't match"); setLoading(false); return; }
        if (formData.new_password.length < 6) { toast.error("Password must be at least 6 characters"); setLoading(false); return; }
        await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: formData.new_password });
        toast.success("Password reset! Please sign in.");
        navigate("/auth", { replace: true });
        setMode("login");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Lock, label: "Secure authorised access" },
    { icon: ChartLineUp, label: "Centralised business operations" },
    { icon: Users, label: "Customer and project coordination" },
  ];

  const titleMap = {
    login: { title: "Welcome Back", subtitle: "Sign in to access your dashboard" },
    register: { title: "Create Account", subtitle: "Register to get started" },
    forgot: { title: "Forgot Password", subtitle: "Enter your email and we'll send you a reset link" },
    reset: { title: "Set New Password", subtitle: tokenEmail ? `Resetting password for ${tokenEmail}` : "Enter a new password" },
  };
  const cta = { login: "Sign In", register: "Create Account", forgot: "Send Reset Link", reset: "Reset Password" };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#002FA7] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <img src="/assets/swiftech-logo.jpg" alt="Swiftech Electronic Security and Technology Solutions" data-testid="auth-logo-desktop" className="w-12 h-12 object-contain bg-white rounded-sm p-1" />
            <div>
              <p className="text-xs text-white/70 tracking-[0.25em] uppercase font-semibold">Swiftech Business Portal</p>
            </div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="auth-left-heading">Secure Access for the Swiftech Team</h2>
          <p className="text-white/70 mt-4 max-w-sm" data-testid="auth-left-description">Authorised team members can securely access Swiftech&apos;s business operations, customer enquiries and internal workflows from one protected portal.</p>
          <div className="mt-12 space-y-4" data-testid="auth-left-supporting-items">
            {features.map((f, i) => {
              const FI = f.icon;
              return (
                <div key={i} className="flex items-center gap-3 text-white/80">
                  <div className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center"><FI size={20} /></div>
                  <span className="font-medium">{f.label}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative z-10 border-t border-white/20 pt-4 flex items-center gap-2" data-testid="auth-left-help">
          <Lock size={16} className="text-white/60" />
          <span className="text-white/60 text-xs">
            Need login assistance? Contact{" "}
            <a href="mailto:support@swiftechindia.com" data-testid="auth-support-email-link" className="underline underline-offset-2 hover:text-white/90">support@swiftechindia.com</a>
          </span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2" data-testid="auth-mobile-brand">
            <img src="/assets/swiftech-logo.jpg" alt="Swiftech Electronic Security and Technology Solutions" data-testid="auth-logo-mobile" className="w-10 h-10 object-contain bg-white rounded-sm p-1" />
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-600">Swiftech Business Portal</span>
          </div>
          <div className="mb-8">
            <h1 data-testid="auth-title" className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
              {titleMap[mode].title}
            </h1>
            <p className="text-slate-500 mt-2">{titleMap[mode].subtitle}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-none p-8 shadow-sm">
            <form data-testid="auth-form" onSubmit={handleSubmit} className="space-y-5">
              {mode === "register" && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Full Name</label>
                  <input data-testid="name-input" type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" required />
                </div>
              )}
              {(mode === "login" || mode === "register" || mode === "forgot") && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Email</label>
                  <input data-testid="email-input" type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none" required />
                </div>
              )}
              {(mode === "login" || mode === "register") && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Password</label>
                    {mode === "login" && (
                      <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
                        className="text-xs text-[#002FA7] hover:underline font-semibold">Forgot password?</button>
                    )}
                  </div>
                  <div className="relative">
                    <input data-testid="password-input" type={showPassword ? "text" : "password"} value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 pr-12 w-full outline-none" required />
                    <button
                      type="button"
                      data-testid="toggle-password-visibility-btn"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-[#002FA7] focus:outline-none focus:text-[#002FA7]"
                      tabIndex={0}
                    >
                      {showPassword ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
                    </button>
                  </div>
                </div>
              )}
              {mode === "reset" && (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">New Password (min 6)</label>
                    <div className="relative">
                      <input data-testid="new-password-input" type={showNewPassword ? "text" : "password"} value={formData.new_password}
                        onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                        className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 pr-12 w-full outline-none" required />
                      <button
                        type="button"
                        data-testid="toggle-new-password-visibility-btn"
                        onClick={() => setShowNewPassword(v => !v)}
                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                        aria-pressed={showNewPassword}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-[#002FA7] focus:outline-none focus:text-[#002FA7]"
                        tabIndex={0}
                      >
                        {showNewPassword ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Confirm Password</label>
                    <div className="relative">
                      <input data-testid="confirm-password-input" type={showConfirmPassword ? "text" : "password"} value={formData.confirm_password}
                        onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                        className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 pr-12 w-full outline-none" required />
                      <button
                        type="button"
                        data-testid="toggle-confirm-password-visibility-btn"
                        onClick={() => setShowConfirmPassword(v => !v)}
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        aria-pressed={showConfirmPassword}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-[#002FA7] focus:outline-none focus:text-[#002FA7]"
                        tabIndex={0}
                      >
                        {showConfirmPassword ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
                      </button>
                    </div>
                  </div>
                </>
              )}
              <button data-testid="auth-submit-btn" type="submit" disabled={loading}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3.5 font-medium w-full transition-colors duration-300 disabled:opacity-50 text-base">
                {loading ? "Processing..." : cta[mode]}
              </button>
            </form>

            <div className="mt-6 text-center space-y-1">
              {mode === "login" && (
                <button data-testid="toggle-auth-mode-btn" onClick={() => setMode("register")} className="text-[#002FA7] hover:underline font-medium text-sm block w-full">
                  Don't have an account? Register
                </button>
              )}
              {mode === "register" && (
                <button data-testid="toggle-auth-mode-btn" onClick={() => setMode("login")} className="text-[#002FA7] hover:underline font-medium text-sm block w-full">
                  Already have an account? Sign In
                </button>
              )}
              {(mode === "forgot" || mode === "reset") && (
                <button data-testid="back-to-login-btn" onClick={() => setMode("login")} className="text-[#002FA7] hover:underline font-medium text-sm block w-full">
                  ← Back to sign in
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 text-center space-y-2">
            <button data-testid="browse-products-link" onClick={() => navigate('/products')} className="text-slate-500 hover:text-[#002FA7] text-sm block w-full">
              Just browsing? View our product catalog
            </button>
            <button data-testid="become-dealer-link" onClick={() => navigate('/dealer-register')} className="text-[#002FA7] hover:underline text-sm font-bold block w-full">
              🏢 Become a SwifTech Dealer →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
